import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import type { PermissionState } from "@capacitor/core";
import { isOrtexApp } from "@/lib/utils";
import type { PushPermission } from "@/lib/store";

// Phone push notifications, shared helpers (ortex/ted/ted#2485).
// Everything here is a no-op outside the native app, and safe on app builds that
// predate the native push setup (the plugin is checked before every use).

export const PUSH_CHANNEL_ID = "ortex_default";

export const isPushSupported = (): boolean => {
    try {
        return isOrtexApp() && Capacitor.isPluginAvailable("PushNotifications");
    } catch {
        return false;
    }
};

export const toPushPermission = (receive: PermissionState): PushPermission => {
    if (receive === "granted") return "granted";
    if (receive === "denied") return "denied";
    // "prompt" and "prompt-with-rationale"
    return "prompt";
};

// --- Native build gate ---

// The first store build with the native push setup (FCM on iOS, POST_NOTIFICATIONS on Android).
// Older builds cannot show the Android 13+ permission dialog or hand iOS an FCM token, so the
// pre-prompt is not offered on them. Override with NEXT_PUBLIC_PUSH_MIN_APP_VERSION when the
// release version is known.
export const PUSH_MIN_APP_VERSION = process.env.NEXT_PUBLIC_PUSH_MIN_APP_VERSION || "1.0.15";

const versionParts = (v: string) =>
    v
        .split(/[^0-9]+/)
        .filter(Boolean)
        .map(Number);

export const isAppVersionAtLeast = (version: string | undefined, min: string = PUSH_MIN_APP_VERSION): boolean => {
    if (!version) return false;
    const a = versionParts(version);
    const b = versionParts(min);
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
        const x = a[i] ?? 0;
        const y = b[i] ?? 0;
        if (x !== y) return x > y;
    }
    return true;
};

// Old iOS builds (no FirebaseMessaging) hand JS the raw 64-hex APNs token, which FCM cannot
// send to. FCM tokens are longer and contain a colon.
export const isFcmToken = (token: string): boolean => !!token && !/^[0-9a-f]{64}$/i.test(token);

// --- Pre-prompt snooze ("Not now" hides the launch pre-prompt for 7 days) ---

const PRE_PROMPT_SNOOZE_KEY = "ortex_push_preprompt_snoozed_at";
const PRE_PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export const isPrePromptSnoozed = (): boolean => {
    try {
        const at = Number(localStorage.getItem(PRE_PROMPT_SNOOZE_KEY));
        return !!at && Date.now() - at < PRE_PROMPT_SNOOZE_MS;
    } catch {
        return false;
    }
};

export const snoozePrePrompt = () => {
    try {
        localStorage.setItem(PRE_PROMPT_SNOOZE_KEY, String(Date.now()));
    } catch {
        // storage unavailable: the pre-prompt may come back next launch
    }
};

// Pages the launch pre-prompt must never cover: sign-in / sign-up flows, embeds and
// the signed-out ad landing pages.
const PRE_PROMPT_BLOCKED_PREFIXES = ["/login", "/login-legacy", "/cognito-login", "/register", "/auth-redirect", "/iframes", "/lp", "/welcome", "/upgrade", "/cancel"];

export const isPrePromptBlockedPath = (pathname: string): boolean => PRE_PROMPT_BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));

// --- Tap routing ---

export interface PushData {
    type?: "alert" | "mi" | "news" | "morning" | "offer" | string;
    route?: string;
    url?: string;
    /** The /staff/push/ send a promotion came from, reported with the tap. */
    pushId?: string;
}

const APP_HOSTS = ["app.ortex.com"];

/**
 * Route a tapped notification. `route` is an in-app path; `url` is either an
 * app.ortex.com link (routed in-app) or an external page (in-app browser).
 * Older payloads sent `route` without the leading slash; both work.
 */
export const routePushTap = (data: PushData | undefined, push: (route: string) => void) => {
    if (!data) return;
    if (typeof data.route === "string" && data.route) {
        const route = data.route.startsWith("/") ? data.route : "/" + data.route;
        // Only relative paths: never let a payload navigate the webview off-site.
        if (!route.startsWith("//")) push(route);
        return;
    }
    if (typeof data.url === "string" && data.url) {
        let parsed: URL;
        try {
            parsed = new URL(data.url);
        } catch {
            return;
        }
        if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return;
        if (APP_HOSTS.includes(parsed.hostname)) {
            push(parsed.pathname + parsed.search + parsed.hash);
        } else {
            Browser.open({ url: parsed.toString() }).catch(() => undefined);
        }
    }
};
